import { useCallback, useContext, useState } from 'react'
import { FiEye } from 'react-icons/fi'
import { Link } from 'react-router-dom'

import { Button } from '~/components/Button'
import { studentCardContext } from '~/components/StudentCard'
import {
  generateComparisonReport,
  openComparisonHtml,
} from '~/modules/evalutation/services'
import { studentRoutes } from '~/routes/routes'
import { getLocalDate } from '~/utils/getLocalDate'

import { EvaluationItem } from './EvaluationItem'

export const EvaluationsArea: React.FC = () => {
  const { student } = useContext(studentCardContext)
  const isMobile = window.matchMedia('(pointer: coarse)').matches

  const [targetEvaluations, setTargetEvaluations] = useState<string[]>([])
  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadPercent, setDownloadPercent] = useState<number | null>(null)
  const [isOpeningHtml, setIsOpeningHtml] = useState(false)

  const handleSelectEvaluation = useCallback((evaluationId: string) => {
    setTargetEvaluations((s) => {
      if (s.length >= 2 && !s.includes(evaluationId)) return s

      return s.includes(evaluationId)
        ? s.filter((i) => i !== evaluationId)
        : [...s, evaluationId]
    })
  }, [])

  const getSortedEvaluations = useCallback(() => {
    if (targetEvaluations.length !== 2) return null

    // Sort evaluations by date to ensure correct order (baseline before current)
    const sortedEvaluations = [...targetEvaluations].sort((a, b) => {
      const evalA = student.evaluations.find((ev) => ev.id === a)
      const evalB = student.evaluations.find((ev) => ev.id === b)
      if (!evalA || !evalB) return 0
      return (
        new Date(evalA.createdAt).getTime() -
        new Date(evalB.createdAt).getTime()
      )
    })

    return {
      baselineEvaluationId: sortedEvaluations[0],
      currentEvaluationId: sortedEvaluations[1],
    }
  }, [targetEvaluations, student.evaluations])

  const handleDownloadPdf = useCallback(async () => {
    const request = getSortedEvaluations()
    if (!request) return

    setIsDownloading(true)
    setDownloadPercent(0)

    try {
      await generateComparisonReport(student.id, request, (percent) => {
        setDownloadPercent(percent)
      })

      setTargetEvaluations([])
    } finally {
      setIsDownloading(false)
      setDownloadPercent(null)
    }
  }, [getSortedEvaluations, student.id])

  const handleOpenHtml = useCallback(async () => {
    const request = getSortedEvaluations()
    if (!request) return

    setIsOpeningHtml(true)

    try {
      await openComparisonHtml(student.id, request)
      setTargetEvaluations([])
    } finally {
      setIsOpeningHtml(false)
    }
  }, [getSortedEvaluations, student.id])

  return (
    <div className='flex flex-col gap-2'>
      <p>Selecione duas avaliação para gerar um comparativo</p>
      <div
        className='flex flex-col gap-2 max-h-96 overflow-y-auto
        overflow-x-hidden bg-slate-100/40 p-2 rounded-lg'
      >
        {student.evaluations.map((ev) => (
          <div key={ev.id} className='flex items-center gap-4'>
            <EvaluationItem
              selected={targetEvaluations.includes(ev.id)}
              onClick={() => handleSelectEvaluation(ev.id)}
              label={getLocalDate(ev.createdAt)}
            />
            <Link
              to={studentRoutes.evaluation(student.id, ev.id)}
              className='p-2 rounded-md bg-gpa-blue-50 hover:bg-gpa-blue-400
            hover:text-white'
            >
              <FiEye size={24} />
            </Link>
          </div>
        ))}
      </div>

      <div className='h-2'></div>

      <div className='flex flex-col gap-2'>
        <Button
          className='w-full'
          disabled={targetEvaluations.length !== 2 || isDownloading}
          onClick={handleDownloadPdf}
        >
          {isDownloading
            ? `Baixando${
                downloadPercent !== null ? ` ${downloadPercent}%` : ''
              }...`
            : 'Baixar PDF'}
        </Button>

        {isDownloading && (
          <div className='h-1 w-full bg-gpa-gray-100 rounded-full overflow-hidden'>
            <div
              className='h-full bg-gpa-blue-500 transition-all'
              style={{ width: `${downloadPercent ?? 0}%` }}
            />
          </div>
        )}

        {!isMobile && (
          <Button
            className='w-full'
            disabled={targetEvaluations.length !== 2 || isOpeningHtml}
            onClick={handleOpenHtml}
          >
            {isOpeningHtml ? 'Abrindo HTML...' : 'Imprimir'}
          </Button>
        )}
      </div>
    </div>
  )
}
